import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  MenuItem,
  TextField,
} from "@mui/material";
import SunIcon from "@mui/icons-material/WbSunny";
import { useDispatch, useSelector } from "react-redux";
import { keyframes } from "@mui/system";
import { yellow } from "@mui/material/colors";
import { unwrapResult } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import * as Yup from "yup";

import { updateUser } from "../features/authSlice";

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email address").required("Required"),
  unit: Yup.string().required("Required"),
  building: Yup.string().required("Required"),
  fullName: Yup.string().required("Required"),
  phone: Yup.string().required("Required"),
});

const UpdateInfoDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [submissionFeedback, setSubmissionFeedback] = useState("");

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      uid: user?.uid,
      email: user?.email || "",
      unit: user?.unit || "",
      building: user?.building || "",
      fullName: user?.fullName || "",
      phone: user?.phone || "",
    },
    validationSchema,
    onSubmit: (values, { setErrors }) => {
      dispatch(updateUser(values))
        .then(unwrapResult)
        .then(() => {
          onClose();
          setErrors({});
        })
        .catch((e) => setSubmissionFeedback(e));
    },
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
    setSubmissionFeedback("");
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton sx={{ margin: "auto", marginTop: 1 }} onClick={handleClose}>
            <SunIcon
              sx={{
                animation: `${rotate} 10s linear infinite`,
                fontSize: "2em",
                color: yellow[500],
              }}
            />
          </IconButton>
        </Box>
        <DialogTitle color="primary" sx={{ textAlign: "center" }}>
          Update Info
        </DialogTitle>
        <DialogContent sx={{ padding: "20px 30px" }}>
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <Typography variant="subtitle2">
            Please review account info below
          </Typography>
          <Typography variant="caption">
            (Guests must stay at Tops'l to reserve chairs)
          </Typography>
          <TextField
            margin="dense"
            label="Full Name"
            type="text"
            fullWidth
            name="fullName"
            value={formik.values.fullName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.fullName && Boolean(formik.errors.fullName)}
            helperText={formik.touched.fullName && formik.errors.fullName}
          />
          <TextField
            margin="dense"
            label="Unit #"
            type="text"
            fullWidth
            name="unit"
            value={formik.values.unit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.unit && Boolean(formik.errors.unit)}
            helperText={formik.touched.unit && formik.errors.unit}
          />
          <TextField
            margin="dense"
            label="Tops'l Building"
            select
            fullWidth
            name="building"
            value={formik.values.building}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.building && Boolean(formik.errors.building)}
            helperText={formik.touched.building && formik.errors.building}
          >
            <MenuItem value=""></MenuItem>
            <MenuItem value="tides">The Tides</MenuItem>
            <MenuItem value="summit">The Summit</MenuItem>
            <MenuItem value="beachManor">Beach Manor</MenuItem>
            <MenuItem value="tennisVillas">Tennis Villas</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Phone"
            type="text"
            fullWidth
            name="phone"
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
          />
        </DialogContent>
        <DialogActions>
          {submissionFeedback && (
            <Typography color="error" sx={{ maxWidth: 380 }}>
              {submissionFeedback}
            </Typography>
          )}
          <Button onClick={handleClose} color="primary" disabled={!user}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Update Info
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateInfoDialog;
